import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { GET_SURVEY } from '../../graphql/queries';
import { useLazyQuery } from '@apollo/client';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { setQuestions, setSurvey } from '../../redux/reducers/survey';
import QuestionsWizard from './components/QuestionsWizard';
import { ISurvey, QuestionType, SurveyStatus } from '../../types';
import { getImage } from '../../helpers/global';
import { storeThemeSettings } from '../../redux/reducers/theme';
import { SurveyProvider } from '../../context/SurveyContext';
import BottomInfo from '../../components/BottomInfo';
import { Helmet } from 'react-helmet';

const dataSurvey = JSON.parse(
  document.getElementById('app-data')?.textContent || '{}',
);

const MainPage: React.FC = () => {
  const { surveyId }: { surveyId: string } = useParams();

  const [callGetSurvey, { loading, data }] = useLazyQuery(GET_SURVEY, {
    variables: { id: surveyId },
  });

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') callGetSurvey();
    // callGetSurvey();
  }, [callGetSurvey]);

  const dispatch = useAppDispatch();

  const { survey, questions } = useAppSelector((state) => state.survey);

  const dispatchData = useCallback(
    (survey: ISurvey) => {
      dispatch(
        setSurvey({
          ...survey,
          backgroundImage: survey.backgroundImage
            ? getImage(survey.backgroundImage)
            : '',
        }),
      );

      dispatch(
        setQuestions(
          survey.questions.map(
            ({
              id,
              type,
              question,
              mandatory,
              skippable,
              skippableText,
              skipRoute,
              icon,
              buttonText,
              nextOnSelect,
              emoticon4Options,
              minimumSelects,
              presetDataSetId,
              presetDataSet,
              externalLink,
              routeToQuestion,
              sortOrder,
              theEnd,
              fieldName,
              answers,
            }: any) => {
              let options = [];

              if (type === QuestionType.PRESET_DATA) {
                options = presetDataSet.presetDataSetDatas.map((dt: any) => ({
                  value: dt.id,
                  label: dt.name,
                }));
              } else if (
                type === QuestionType.MULTI_SELECT ||
                type === QuestionType.RANKING ||
                type === QuestionType.EMOTICON ||
                type === QuestionType.SINGLE_SELECT
              ) {
                options = answers.map((elm: any) => {
                  return {
                    value: elm.id,
                    label: elm.data.answerText,
                    icon: elm.data.icon ? getImage(elm.data.icon) : '',
                    routeId: elm.data.routeToQuestion,
                    isOther: elm.data.isOther,
                  };
                });
              }

              // make 'mailto' clickable
              var regex = /\[mailto:(.*?)\](.*?)\[\/mailto]/;
              var mailtoString: any;
              if ((mailtoString = regex.exec(question))) {
                question = question.replace(
                  mailtoString[0],
                  `<a href='mailto:${mailtoString[1]}'><strong><u>${mailtoString[2]}</u></strong></a>`,
                );
              }

              return {
                id,
                type,
                copy: question,
                mandatory,
                skip_question: skippable,
                skip_copy: skippableText,
                skipRoute,
                icon: icon ? getImage(icon) : '',
                button_copy: buttonText,
                nextOnSelect,
                emoticon4Options,
                routeToQuestion,
                minimumSelects,
                presetDataSetId,
                externalLink,
                sortOrder,
                theEnd,
                fieldName,
                answers,
                options,
              };
            },
          ) || [],
        ),
      );

      dispatch(
        storeThemeSettings({
          backgroundColor: survey.backgroundColor || 'white',
          logoPosition:
            (survey.logoAlignment as 'LEFT' | 'CENTER' | 'RIGHT') || 'RIGHT',
          primaryColor: '#E5007F',
          textColor: survey.fontColor || 'black',
          buttonBackgroundColor: survey.buttonColor || 'white',
          buttonColor: survey.buttonTextColor || 'black',
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (Object.keys(dataSurvey).length === 0) return;
    dispatchData(dataSurvey);
  }, [dispatchData]);

  useEffect(() => {
    if (data && data.getSurvey) {
      const surveyInfo = data.getSurvey as ISurvey;
      dispatchData(surveyInfo);
    }
  }, [data, dispatchData]);

  const isSurveyAvailable = useMemo(() => {
    return survey && questions.length >= 1;
  }, [survey, questions]);

  return (
    <>
      <Helmet>
        {survey?.teamWhiteLabelFavicon && (
          <link
            rel="shortcut icon"
            href={getImage(survey?.teamWhiteLabelFavicon)}
          />
        )}
        <title>{survey?.name}</title>
      </Helmet>
      <SurveyProvider>
        <Box h="100%">
          {loading && <Text>Loading...</Text>}

          {!loading && isSurveyAvailable && (
            <Flex direction="column" h="100%">
              <QuestionsWizard
                survey={survey as ISurvey}
                questions={questions}
              />

              {survey?.status !== SurveyStatus.PUBLISHED && <BottomInfo />}
            </Flex>
          )}

          {!loading && !isSurveyAvailable && <Text>Survey Not Found!</Text>}
        </Box>
      </SurveyProvider>
    </>
  );
};

export default MainPage;
